import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ProfileService } from 'app/modules/profile/profile.service';
import { FuseConfigService } from '@fuse/services/config';
import { environment } from 'environments/environment';
import { apiMessageOptions } from 'app/shared/interceptors/api-message-context';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class AuthService {
    private readonly _tokenInterval: number = 15 * 60 * 1000;

    constructor(
        private _httpClient: HttpClient,
        private _userService: ProfileService,
        private _fuseConfigService: FuseConfigService
    ) {
    }

    set accessToken(token: string) {
        if (environment.user == undefined || environment.user === null) {
            environment.user = {} as any;
        }
        environment.user.token = token;
    }

    get accessToken(): string {
        return environment.user?.token || '';
    }

    get isAuthenticated(): boolean {
        return !!environment.user?.token;
    }

    get isTokenExpired(): boolean {
        const expires = environment.user?.expires ?? 0;
        return Date.now() >= expires;
    }

    signIn(credentials: { email: string, password: string }): Observable<any> {

        // if (environment.user?.token) {
        //     console.error('Sign-in blocked: user is already marked as authenticated.');
        //     return throwError(() => new Error('User is already logged in.'));
        // }

        return this._httpClient
            .post(`/api/session/sign-in`, credentials, apiMessageOptions(false))
            .pipe(
                switchMap((response: any) => {
                    environment.user.expires = (Date.now() + this._tokenInterval);
                    this._process_response(response);
                    return of(response);
                }),
                catchError((error) => {
                    console.error('Sign-in request failed.', { endpoint: `/api/session/sign-in`, error });
                    return throwError(() => error);
                })
            );
    }

    signInUsingToken(): Observable<any> {
        if (!this.isAuthenticated || this.isTokenExpired) {
            return of(false);
        }

        return this._httpClient
            .post(`/api/session/refresh-token`, {}, apiMessageOptions(false))
            .pipe(
                switchMap((response: any) => {
                    environment.user.expires = (Date.now() + this._tokenInterval);
                    this._process_response(response);
                    return of(response);
                }),
                catchError((error) => {
                    console.error('Token refresh failed.', { endpoint: `/api/session/refresh-token`, error });

                    environment.user = {};
                    environment.selection = { organization: undefined, branch: undefined };
                    environment.save();

                    return of(false);
                })
            );
    }

    signOut(): Observable<any> {
        return this
            ._httpClient.post(`/api/session/sign-out`, {}, apiMessageOptions(false))
            .pipe(
                catchError(() => of(true)),
                switchMap(() => {

                    environment.user = {};
                    environment.selection = { organization: undefined, branch: undefined };
                    environment.save();

                    return of(false);
                })
            );
    }

    check(): Observable<boolean> {
        if (this.isAuthenticated && !this.isTokenExpired) {
            return of(true);
        }
        return of(false);
    }











    /**
     * Forgot password
     *
     * @param user
     */
    forgotPassword(user: { email: string }): Observable<any> {
        return this._httpClient.post(
            `/api/session/forget-password`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Activate account
     *
     * @param user
     */
    activateAccount(user: { email: string }): Observable<any> {
        return this._httpClient.post(
            `/api/session/activate-account`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Validate OTP
     *
     * @param user
     */
    validateOTP(user: { email: string, code: string, reference: string, action: string }): Observable<any> {
        return this._httpClient.post(
            `/api/session/validate-otp`,
            user,
            apiMessageOptions(false)
        ).pipe(
            catchError((error: HttpErrorResponse) => {
                // Backward-compatible fallback when the session endpoint is not deployed yet.
                if (error?.status === 404 || error?.status === 405) {
                    return this._httpClient.post(
                        `/api/dashboard/validate-otp`,
                        user,
                        apiMessageOptions(false)
                    );
                }

                return throwError(() => error);
            })
        );
    }

    /**
     * Reset password
     *
     * @param user  
     */
    resetPassword(user: { email: string, reference: string, otp: string, password: string, password_confirm: string }): Observable<any> {
        return this._httpClient.post(
            `/api/session/reset-password`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Sign out
     */


    impersonate(email: string): Observable<any> {
        const targetEmail = `${email || ''}`.trim();
        if (!targetEmail) return throwError(() => new Error('Target user email is required for impersonation.'));

        return this._httpClient.post(
            `/api/session/impersonate`,
            { email: targetEmail },
            apiMessageOptions(false)
        ).pipe(
            switchMap((response: any) => {
                this._process_response(response);
                return of(response);
            }),
            catchError((error) => {
                return throwError(() => error);
            })
        );
    }

    unimpersonate(): Observable<boolean> {
        this.accessToken = environment.user?.impersonator?.token || '';

        return this.signInUsingToken().pipe(
            map((ok) => {
                if (ok) {
                    try {
                        this._userService.selectedOrganization = environment.selection?.organization || null;
                    } catch (e) {
                        this._userService.selectedOrganization = null;
                    }
                    try {
                        this._userService.selectedBranch = environment.selection?.branch || null;
                    } catch (e) {
                        this._userService.selectedBranch = null;
                    }

                    return true;
                }
                return false;
            }),
            catchError(() => of(false))
        );
    }

    getStoredUser(): any | null {
        return environment.user?.profile || null;
    }

    isWorkspaceSelectionRequired(): boolean {
        if (!this.accessToken) {
            return false;
        }
        return !environment.user.roles?.super;
    }

    hasWorkspaceSelection(): boolean {
        const organization = environment.selection.organization;
        const branch = environment.selection.branch;

        return !!organization?.code && !!branch?.code;
    }

    hasWorkspaceAccess(): boolean {
        if (!this.isWorkspaceSelectionRequired()) {
            return true;
        }

        return this.hasWorkspaceSelection();
    }

    private _restoreSavedWorkspaceSelection(organizations: any[], user: any = null): void {
        // Restore the last saved organization/branch from environment.selection
        // (it was persisted there before the session refresh)
        const savedOrganization = environment.selection.organization;
        const savedBranch = environment.selection.branch;

        const matchedOrganization = savedOrganization?.code
            ? organizations.find((o: any) => o?.code === savedOrganization.code) || null
            : null;

        const matchedBranch = matchedOrganization && savedBranch?.code
            ? (matchedOrganization?.branches || []).find((b: any) => b?.code === savedBranch.code) || null
            : null;

        if (matchedOrganization && matchedBranch) {
            this._userService.selectedOrganization = matchedOrganization;
            this._userService.selectedBranch = matchedBranch;

            // const normalizedUser = this._normalizeUser(user, organizations, matchedOrganization, matchedBranch);
            // if (normalizedUser) {
            //     this._userService.user = normalizedUser;
            // }

            if (matchedOrganization?.config) {
                this._fuseConfigService.applyPermanentLayoutChanges(matchedOrganization.config);
            }
            return;
        }

        this._userService.selectedOrganization = null;
        this._userService.selectedBranch = null;
    }

    private _process_response(payload: any): any {
        environment.user.token = payload?.token;
        environment.user.superadmin = payload?.user?.superadmin;
        environment.user.profile = payload?.user;
        environment.user.organizations = payload?.organizations;
        environment.user.access = payload?.access;

        environment.app = payload?.app || [];

        environment.save();
    }

    // private _extractPayload(response: any): any {
    //     return response?.payload && typeof response.payload === 'object'
    //         ? response.payload
    //         : response;
    // }

    // private _normalizeOrganizations(organizations: any): any[] {
    //     if (Array.isArray(organizations)) {
    //         return organizations.map((organization: any) => this._normalizeOrganization(organization));
    //     }

    //     if (organizations && typeof organizations === 'object') {
    //         return Object.values(organizations).map((organization: any) => this._normalizeOrganization(organization));
    //     }

    //     return [];
    // }

    // private _normalizeOrganization(organization: any): any {
    //     const branches = Array.isArray(organization?.branches)
    //         ? organization.branches
    //         : organization?.branches && typeof organization.branches === 'object'
    //             ? Object.values(organization.branches)
    //             : [];

    //     return {
    //         ...organization,
    //         branches
    //     };
    // }

    // private _normalizeUser(user: any, organizations: any[], selectedOrganization: any = null, selectedBranch: any = null): any {
    //     if (!user) {
    //         return null;
    //     }

    //     const roleMap = this._extractRoleMap(user?.role) || this._extractRoleMap(user?.extra?.role_map);
    //     const resolvedRoles = this._resolveEffectiveRoles(roleMap || user?.role, organizations, selectedOrganization, selectedBranch, user);

    //     return {
    //         ...user,
    //         role: resolvedRoles,
    //         extra: {
    //             ...(user?.extra || {}),
    //             role_map: roleMap || user?.extra?.role_map || null
    //         }
    //     };
    // }

    // private _extractRoleMap(value: any): Record<string, any> | null {
    //     return value && typeof value === 'object' && !Array.isArray(value)
    //         ? value as Record<string, any>
    //         : null;
    // }

    // private _resolveEffectiveRoles(roleSource: any, organizations: any[] = [], selectedOrganization: any = null, selectedBranch: any = null, user: any = null): string[] {
    //     const roleMap = this._extractRoleMap(roleSource);
    //     const fallbackOrganizationCode = selectedOrganization?.code
    //         || organizations?.[0]?.code
    //         || Object.keys(roleMap || {})[0]
    //         || null;

    //     return AuthUtils.resolveEffectiveRoles(roleSource, {
    //         selectedOrganizationCode: selectedOrganization?.code || null,
    //         selectedBranchCode: selectedBranch?.code || null,
    //         fallbackOrganizationCode,
    //         user
    //     });
    // }

    // private _parseStoredSelection(storageKey: AuthStorageKey): any | null {
    //     if (storageKey === AUTH_STORAGE_KEYS.impersonatorSelectedOrganization) {
    //         return AuthStorage.getJson(storageKey);
    //     }
    //     if (storageKey === AUTH_STORAGE_KEYS.impersonatorSelectedBranch) {
    //         return AuthStorage.getJson(storageKey);
    //     }
    //     return null;
    // }

    // handleUnauthorized(error: any, requestUrl: string): void {
    //     if (this._unauthorizedHandled) {
    //         return;
    //     }

    //     this._unauthorizedHandled = true;

    //     console.error('[AuthService] Unauthorized response detected. Clearing session and redirecting to sign-in.', {
    //         requestUrl,
    //         error
    //     });

    //     localStorage.removeItem('access_token');
    //     localStorage.removeItem('impersonator_access_token');
    //     this._authenticated = false;

    //     setTimeout(() => {
    //         this._unauthorizedHandled = false;
    //     }, 3000);

    //     window.location.assign('/sign-in');
    // }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string, email: string, password: string, company: string }): Observable<any> {
        return this._httpClient.post(
            'api/auth/sign-up',
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string, password: string }): Observable<any> {
        return this._httpClient.post(
            'api/auth/unlock-session',
            credentials,
            apiMessageOptions(false)
        );
    }

}